import { Card } from '../../components/ui.js';
import { ProjectStatusBadge, StoryStatusBadge } from '../../status.js';
import type { ProjectStatus, StoryStatus } from '../../api/types.js';

/** The founder's journey, start to finish. */
const STEPS: { title: string; body: string }[] = [
  {
    title: 'Tell us about your idea',
    body: 'You answer a short questionnaire about what you want to build and who it’s for — or, if you already have a brief or spec document, you can upload or paste it whole and we’ll use that instead. There are no wrong answers; where something’s unclear, we make a sensible assumption and note it.',
  },
  {
    title: 'We write the specification',
    body: 'We turn your answers (or your document) into a clear specification — the blueprint for your app. You review it and approve it (or ask for changes) before anything is built.',
  },
  {
    title: 'We break it into user stories',
    body: 'The approved specification is split into small “user stories” — bite-sized pieces of the app, each built and reviewed on its own so progress is steady and visible.',
  },
  {
    title: 'You approve, we build',
    body: 'You approve each story, then our developers build it. You can follow along, and a story that needs your input will show up as “Ready for approval”.',
  },
  {
    title: 'Try it live, then sign off',
    body: 'Each built story is deployed to your live app so you can try it for real. Once it’s right, it’s marked tested and considered done.',
  },
  {
    title: 'Live and improving',
    body: 'Your app runs at its own web address (see the Live testing tab), and we keep refining and adding to it, story by story.',
  },
];

/** What each project status means, in the founder's words. */
const PROJECT_STATUSES: { status: ProjectStatus; text: string }[] = [
  { status: 'questionnaire', text: 'You’re answering the questionnaire about your idea.' },
  { status: 'generating_spec', text: 'We’re drafting your specification from your answers.' },
  { status: 'spec_review', text: 'Our team is reviewing the draft before sharing it with you.' },
  {
    status: 'spec_shared',
    text: 'Your specification is ready — it’s waiting for you to review and approve it.',
  },
  {
    status: 'spec_approved',
    text: 'You’ve approved the specification; we’re getting ready to build.',
  },
  { status: 'generating_stories', text: 'We’re breaking the specification into user stories.' },
  { status: 'building', text: 'Your stories are being built.' },
  {
    status: 'ongoing_refinement',
    text: 'Your app is live and we’re continuing to refine and add to it.',
  },
  {
    status: 'archived',
    text: 'This project is paused. You can resume it any time from your home page.',
  },
];

/** What each story status means. */
const STORY_STATUSES: { status: StoryStatus; text: string }[] = [
  { status: 'draft', text: 'An early draft of the story — not ready for you to review yet.' },
  { status: 'in_review', text: 'Our team is refining the story.' },
  {
    status: 'ready',
    text: 'The story is ready for you to review and approve so it can be built.',
  },
  { status: 'approved', text: 'You’ve approved this story; it’s queued to be built.' },
  { status: 'building', text: 'This story is being built right now.' },
  {
    status: 'built',
    text: 'The story is built and deployed to your live app — it’s being tested before sign-off.',
  },
  { status: 'accepted', text: 'The story is finished, tested, and signed off. ✅' },
];

/**
 * Founder-facing help: how the process works, and what every project and story
 * status they might see actually means — with the same badges shown elsewhere,
 * so they can match what they read to what they see.
 */
export function GuidePage() {
  return (
    <div className="flex flex-col gap-6">
      <Card>
        <h2 className="mb-2 font-title text-2xl tracking-wide">How your project works</h2>
        <p className="mb-4 text-sm leading-relaxed opacity-80">
          From your first answers to a live app, here’s the journey — each step keeps you in
          control, with nothing built until you’ve approved it.
        </p>
        <ol className="flex flex-col gap-4">
          {STEPS.map((step, i) => (
            <li key={i} className="flex gap-3">
              <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/15 text-sm font-semibold text-primary">
                {i + 1}
              </span>
              <div>
                <div className="font-medium">{step.title}</div>
                <p className="text-sm leading-relaxed opacity-80">{step.body}</p>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-5 rounded-md border border-primary/30 bg-primary/5 p-4 text-sm">
          <p className="font-medium">Already have a spec or brief?</p>
          <p className="mt-1 leading-relaxed opacity-80">
            You don’t have to fill in the questionnaire. On the{' '}
            <span className="font-medium">Questionnaire</span> tab you can upload or paste your own
            document — a brief, a full spec, or just your notes — as a whole, and we’ll build the
            specification from that instead.
          </p>
        </div>
      </Card>

      <Card>
        <h2 className="mb-1 font-title text-2xl tracking-wide">Project status</h2>
        <p className="mb-4 text-sm opacity-70">
          The badge at the top of your project shows where the whole project is.
        </p>
        <ul className="flex flex-col gap-3">
          {PROJECT_STATUSES.map((row) => (
            <li key={row.status} className="flex flex-wrap items-center gap-3">
              <span className="w-40 flex-shrink-0">
                <ProjectStatusBadge status={row.status} />
              </span>
              <span className="min-w-0 flex-1 text-sm opacity-80">{row.text}</span>
            </li>
          ))}
        </ul>
      </Card>

      <Card>
        <h2 className="mb-1 font-title text-2xl tracking-wide">Story status</h2>
        <p className="mb-4 text-sm opacity-70">
          Each user story carries its own status as it moves from idea to done.
        </p>
        <ul className="flex flex-col gap-3">
          {STORY_STATUSES.map((row) => (
            <li key={row.status} className="flex flex-wrap items-center gap-3">
              <span className="w-40 flex-shrink-0">
                <StoryStatusBadge status={row.status} />
              </span>
              <span className="min-w-0 flex-1 text-sm opacity-80">{row.text}</span>
            </li>
          ))}
        </ul>
      </Card>

      <Card>
        <h2 className="mb-1 font-title text-2xl tracking-wide">Need a hand?</h2>
        <p className="text-sm leading-relaxed opacity-80">
          Use the <span className="font-medium">Messages</span> tab any time to ask our team a
          question — we’re here to help at every step.
        </p>
      </Card>
    </div>
  );
}
